import React, { useEffect, useState } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeProvider } from "styled-components/macro";
import { use100vh } from "react-div-100vh";
import { get } from "lodash";

import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import mediaBreakpoints from "./utilities/media-sc-breakpoints";
import { BreakpointProvider } from "./utilities/media-js-breakpoints";

import Configurator from "./pages/Configurator";
import ErrorAlert from "./components/Alerts/ErrorAlert";
import { CREATE_SESSION } from "./store/actions/sessionActions";

import { AppLoader } from "./components/Loader/AppLoader";
import { RESOLUTION_CHANGED } from "./store/actions/viewActions";
import { REGISTER_ACTIVITY } from "./store/actions/timeoutActions";
import { getAppConfig, getConfigurationString, getFrontendSettings, getIsLoading } from "./store/sagas/selectors";

import "material-icons/iconfont/material-icons.css";

const AppContainer = styled.div`
    font-family: ${props => props.theme.fontFamily};
    color: ${props => props.theme.textColor};
    background: ${props => props.theme.background.color};
`;

const App = () => {
    const maxHeight: number | null = use100vh();

    const dispatch = useDispatch();

    const isLoading = useSelector(getIsLoading);
    const frontendSettings = useSelector(getFrontendSettings);
    const appConfig = useSelector(getAppConfig);
    const configurationString = useSelector(getConfigurationString);

    const [config, setConfig] = useState<any>({});

    const resetTimer = () => {
        dispatch({ type: REGISTER_ACTIVITY });
    };
    document.onclick = resetTimer;
    document.onkeydown = resetTimer;
    document.ontouchstart = resetTimer;
    document.onscroll = resetTimer;

    const setStyleProperties = (configProperties: any) => {
        const r = document.documentElement;
        r.style.setProperty("--primary-color", configProperties.colors.primaryColor);
        r.style.setProperty("--primary-hover-color", configProperties.colors.primaryHoverColor);
        r.style.setProperty("--primary-active-color", configProperties.colors.primaryActiveColor);
        r.style.setProperty("--light-hover-color", configProperties.colors.lightHoverColor);
        r.style.setProperty("--light-active-color", configProperties.colors.lightActiveColor);
    };

    useEffect(() => {
        const handleResize = () => {
            dispatch({ type: RESOLUTION_CHANGED });
        };
        window.addEventListener("resize", handleResize);

        const handleMessage = (event: MessageEvent) => {
            if (event.data.type) dispatch(event.data);
        };
        window.addEventListener("message", handleMessage);

        dispatch({ type: CREATE_SESSION });

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("message", handleMessage);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!appConfig) return;

        const breakpoints = { bp: mediaBreakpoints };
        setConfig({ ...appConfig.style, ...breakpoints });
        setStyleProperties(appConfig);
    }, [appConfig]);

    /*
    Removes the config_string from URL if specified.
    useEffect(() => {
        if (!frontendSettings) return;
        if (!frontendSettings.enable_url_configuration_state) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete("config_string");
            const searchParamsString = searchParams.toString();
            const newRelativePathQuery = decodeURIComponent(
                `${window.location.pathname}${searchParamsString.length ? `?${searchParamsString}` : ""}`
            );
            window.history.pushState(null, "", newRelativePathQuery);
        }
    }, [frontendSettings]);
    */

    useEffect(() => {
        if (!frontendSettings) return;
        if (!configurationString || !frontendSettings.enable_url_configuration_state) return;

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set("config_string", configurationString);
        const newRelativePathQuery = decodeURIComponent(`${window.location.pathname}?${searchParams.toString()}`);
        window.history.pushState(null, "", newRelativePathQuery);
    }, [configurationString, frontendSettings]);

    if (isLoading) {
        // Set the first loader to gray until styling.json has been loaded.
        const loaderColor = get(config, "appLoader.color", "#888");
        return (
            <AppLoader
                type="Grid"
                color={loaderColor}
                background={get(config, "appLoader.backgroundColor")}
                height={200}
                width={80}
            />
        );
    }

    return (
        <BreakpointProvider>
            <ThemeProvider theme={config}>
                <BrowserRouter>
                    <Switch>
                        <AppContainer style={{ height: maxHeight || "100vh" }}>
                            <Route exact strict path="*/" component={Configurator} />
                        </AppContainer>
                    </Switch>
                </BrowserRouter>
                <ErrorAlert />
            </ThemeProvider>
        </BreakpointProvider>
    );
};

export default App;
